/**
 * @deprecated Use the Battlefield Editor's native Background tool instead.
 * The editor stores universal bgScale/offset values that work in all contexts.
 * This tool stores values relative to the parallax contain-fit system, which
 * requires translation and doesn't produce portable alignment data.
 *
 * GridPositionEditor (aliased as BackgroundPositionEditor for import compat)
 * Dev-only tool for visually positioning the hex grid relative to the battlefield background.
 *
 * Toggle with Shift+B on the battle page.
 * Drag to reposition the grid, scroll to scale the background image.
 * Values are logged to console on deactivation for pasting into theme config.
 */

import * as PIXI from 'pixi.js';
import { initializeSpriteBasePosition } from './parallax-updater';

export class BackgroundPositionEditor {
  private containers: PIXI.Container[];
  private bgSprite: PIXI.Sprite | null;
  private canvas: HTMLCanvasElement;
  private hud: HTMLDivElement | null = null;

  private isDragging = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private containerStartPositions: { x: number; y: number }[] = [];

  // Accumulated offset from original positions
  private offsetX = 0;
  private offsetY = 0;
  private scaleMultiplier = 1.0;

  private originalPositions: { x: number; y: number }[];
  private originalBgScale: number;

  // Bound handlers for cleanup
  private onMouseDown: (e: MouseEvent) => void;
  private onMouseMove: (e: MouseEvent) => void;
  private onMouseUp: (e: MouseEvent) => void;
  private onWheel: (e: WheelEvent) => void;

  /**
   * @param containers - Gameplay containers to move (grid, creatures, effects, etc.)
   * @param bgSprite - Background sprite for scroll-to-scale (or null if unavailable)
   * @param canvas - The PixiJS canvas element for event binding
   */
  constructor(containers: PIXI.Container[], bgSprite: PIXI.Sprite | null, canvas: HTMLCanvasElement) {
    this.containers = containers;
    this.bgSprite = bgSprite;
    this.canvas = canvas;

    this.originalPositions = containers.map(c => ({ x: c.x, y: c.y }));
    this.originalBgScale = bgSprite ? ((bgSprite as any).__baseScale ?? bgSprite.scale.x) : 1;

    // Bind handlers
    this.onMouseDown = this.handleMouseDown.bind(this);
    this.onMouseMove = this.handleMouseMove.bind(this);
    this.onMouseUp = this.handleMouseUp.bind(this);
    this.onWheel = this.handleWheel.bind(this);

    this.activate();
  }

  private activate(): void {
    this.canvas.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mouseup', this.onMouseUp);
    this.canvas.addEventListener('wheel', this.onWheel, { passive: false });

    this.createHUD();
    this.updateHUD();
    console.log('[GridPositionEditor] Activated — drag to move grid, scroll to scale background, Shift+B to finish');
  }

  destroy(): void {
    this.canvas.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mouseup', this.onMouseUp);
    this.canvas.removeEventListener('wheel', this.onWheel);

    if (this.hud && this.hud.parentNode) {
      this.hud.parentNode.removeChild(this.hud);
      this.hud = null;
    }

    // Update background base position so parallax continues correctly
    if (this.bgSprite) {
      initializeSpriteBasePosition(this.bgSprite);
    }

    // Log final values for theme config
    console.log(
      `[GridPositionEditor] Final values:\n` +
      `  gridOffsetX: ${Math.round(this.offsetX)},\n` +
      `  gridOffsetY: ${Math.round(this.offsetY)},\n` +
      `  bgScale: ${this.scaleMultiplier.toFixed(3)}\n\n` +
      `Paste into theme config:\n` +
      `  near: { factor: 0.5, scale: ${this.scaleMultiplier.toFixed(3)}, gridOffsetX: ${Math.round(this.offsetX)}, gridOffsetY: ${Math.round(this.offsetY)} }`
    );
  }

  private handleMouseDown(e: MouseEvent): void {
    if (e.button !== 0) return;

    this.isDragging = true;
    this.dragStartX = e.clientX;
    this.dragStartY = e.clientY;
    this.containerStartPositions = this.containers.map(c => ({ x: c.x, y: c.y }));

    e.preventDefault();
    e.stopPropagation();
  }

  private handleMouseMove(e: MouseEvent): void {
    if (!this.isDragging) return;

    const dx = e.clientX - this.dragStartX;
    const dy = e.clientY - this.dragStartY;

    // Move all gameplay containers (world-space, account for zoom)
    const worldScale = this.getWorldScale();
    for (let i = 0; i < this.containers.length; i++) {
      this.containers[i].x = this.containerStartPositions[i].x + dx / worldScale;
      this.containers[i].y = this.containerStartPositions[i].y + dy / worldScale;
    }

    this.offsetX = this.containers[0].x - this.originalPositions[0].x;
    this.offsetY = this.containers[0].y - this.originalPositions[0].y;

    this.updateHUD();
  }

  private handleMouseUp(_e: MouseEvent): void {
    this.isDragging = false;
  }

  private handleWheel(e: WheelEvent): void {
    if (!this.bgSprite) return;

    e.preventDefault();
    e.stopPropagation();

    const delta = e.deltaY > 0 ? -0.05 : 0.05;
    this.scaleMultiplier = Math.max(0.2, Math.min(5.0, this.scaleMultiplier + delta));

    const newScale = this.originalBgScale * this.scaleMultiplier;
    this.bgSprite.scale.set(newScale);
    (this.bgSprite as any).__baseScale = newScale;

    this.updateHUD();
  }

  private getWorldScale(): number {
    // Walk up the parent chain to find the effective scale
    let node: PIXI.Container | null = this.containers[0]?.parent;
    let scale = 1;
    while (node) {
      scale *= node.scale.x;
      node = node.parent;
    }
    return scale || 1;
  }

  private createHUD(): void {
    this.hud = document.createElement('div');
    this.hud.style.cssText = `
      position: fixed;
      top: 10px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(0, 0, 0, 0.85);
      color: #00ff88;
      font-family: monospace;
      font-size: 14px;
      padding: 12px 20px;
      border-radius: 8px;
      border: 1px solid #00ff88;
      z-index: 10000;
      pointer-events: none;
      white-space: pre;
    `;
    document.body.appendChild(this.hud);
  }

  private updateHUD(): void {
    if (!this.hud) return;
    this.hud.textContent =
      `GRID POSITION EDITOR  [Shift+B to finish]\n` +
      `drag: move grid  |  scroll: scale background\n` +
      `gridOffsetX: ${Math.round(this.offsetX)}  gridOffsetY: ${Math.round(this.offsetY)}  bgScale: ${this.scaleMultiplier.toFixed(3)}`;
  }
}
